import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { AlertController, ToastController } from '@ionic/angular';
import { NaploService } from '../naplo.service';
import { DataService } from '../../data.service';

/**
 *  SZÁMLA BEHÚZÁSA A NAV-BÓL (Online Számla, OSA) – közös választó a két könyvelőablakhoz.
 *
 *  Korábban két külön felület volt: egyszeres könyvelésnél a Könyvelés ablak OSA-modálja,
 *  kettősnél egy önálló „Online Számla" oldal (a NAV-kulcsokkal egy lapon), ráadásul két
 *  külön menüpontból. Most egy választó van, és a könyvelőablakból nyílik („Számla behúzása"):
 *
 *    - megnyitáskor AZONNAL a tárolt (korábban letöltött) számlák látszanak, és a háttérben
 *      AUTOMATIKUSAN indul a NAV-lekérés a könyvelési évre – nem kell külön „lekérni";
 *      egy ügyfél/irány párra munkamenetenként egyszer (utána a „Frissítés a NAV-tól" gomb);
 *    - a lekérés háttérfeladat (backend /osa/digest-inditas): elnavigálhatsz, a fejlécben követheted;
 *    - régebbi időszak ELŐNÉZETTEL is lekérdezhető, és csak a kijelöltek emelődnek be.
 *
 *  A könyvelés módja a HÍVÓ dolga (egyszeres: rovatos tétel; kettős: kontírozott bizonylat):
 *  a választó csak jelez (`konyvel`, `tomegesKonyvel`, `kep`).
 */
@Component({
  selector: 'app-naplo-osa-valaszto',
  templateUrl: './osa-valaszto.component.html',
  styleUrls: ['../naplo-shared.scss', './osa-valaszto.component.scss'],
})
export class OsaValasztoComponent implements OnInit, OnDestroy {
  @Input() ugyfelId: string | null = null;
  @Input() ev: number = new Date().getFullYear();
  /** Kezdő irány: 'inbound' = bejövő (beszerzés), 'outbound' = kimenő (értékesítés). */
  @Input() irany: 'inbound' | 'outbound' = 'inbound';
  /** Csoportos könyvelés engedélyezése (egyszeres könyvelésnél van rá végpont). */
  @Input() tomeges = false;
  /** Kettős könyvelésnél a könyvelt számlák halmaza (egyszeresnél a tárolt sor `konyvelt` mezője dönt). */
  @Input() konyveltSzamlak: Set<string> | null = null;

  @Output() konyvel = new EventEmitter<any>();
  @Output() tomegesKonyvel = new EventEmitter<any[]>();
  @Output() kep = new EventEmitter<any>();
  /** Módosító számla csatolása az eredetihez könyvelés nélkül (0 Ft-os módosító). */
  @Output() csatol = new EventEmitter<any>();
  /** Kettős könyvelésnél az eredetihez csatolt módosítók (egyszeresnél a tárolt sor `csatolt` mezője). */
  @Input() csatoltSzamlak: Set<string> | null = null;

  /*  A NAV-LEKÉRÉS NEM INDUL EL MAGÁTÓL az ablak megnyitásakor.
   *
   *  Korábban igen (munkamenetenként egyszer, ügyfél/irány/év szerint), és ez rossz volt: a
   *  „Számla behúzása" gombra kattintva rögtön elindult egy percekig tartó NAV-lekérés, akkor is,
   *  ha a könyvelő csak a MÁR letöltött listából akart egy számlát kiválasztani. Az ablak ezért a
   *  tárolt (korábban lekért) listát mutatja, a friss lekérést a „Lekérés a NAV-tól" gomb indítja.
   *  Az időzített szinkron (Szervíz → Szinkronizálás) változatlanul hozza a friss számlákat. */

  szamlak: any[] = [];
  betoltes = false;
  feladatId: string | null = null;
  halad: any = null;
  navUzenet = '';
  keresoSzoveg = '';
  csakKonyveletlen = true;
  reszletesNyit = false;
  elonezet = false;
  beemelFut = false;
  kijelolt = new Set<string>();
  idoszak = { tol: '', ig: '' };
  private el = true;

  //  Nézet: a számlák mellett a Kassza napi NYUGTA-összesítői is behúzhatók és bevételként könyvelhetők.
  nezet: 'szamla' | 'nyugta' = 'szamla';
  nyugtak: any[] = [];
  nyugtaBetoltes = false;
  nyugtaFut = false;
  get nyugtaKonyveletlenDb(): number { return this.nyugtak.filter((n) => !n.konyvelt).length; }

  //  Közvetlen NAV eNyugta-lekérés (a kliens NAV-userével) – a cég napi nyugta-adatszolgáltatásai a NAV-ból.
  enyugtaTol = '';
  enyugtaIg = '';
  enyugtaNapok: any[] = [];
  enyugtaFut = false;
  enyugtaUzenet = '';
  enyugtaTeszt = false;
  async enyugtaLeker() {
    if (!this.ugyfelId) return;
    if (!this.enyugtaTol || !this.enyugtaIg) {
      const ma = new Date().toISOString().slice(0, 10);
      this.enyugtaTol = this.enyugtaTol || `${this.ev}-${String(new Date().getMonth() + 1).padStart(2, '0')}-01`;
      this.enyugtaIg = this.enyugtaIg || ma;
    }
    this.enyugtaFut = true; this.enyugtaUzenet = '';
    try {
      const r: any = await this.naplo.get(`/enyugta/${this.ugyfelId}?tol=${this.enyugtaTol}&ig=${this.enyugtaIg}`);
      this.enyugtaNapok = r?.napok || [];
      this.enyugtaTeszt = !!r?.teszt;
      this.enyugtaUzenet = `${this.enyugtaNapok.length} napi nyugta-adatszolgáltatás a NAV eNyugtából${r?.teszt ? ' (TESZT környezet)' : ''}.`;
    } catch (e: any) { this.enyugtaNapok = []; this.enyugtaUzenet = ''; this.uzen(e?.error?.error || 'A NAV eNyugta lekérés nem sikerült.', true); }
    finally { this.enyugtaFut = false; }
  }
  //  Egy napi adatszolgáltatás kinyitása: tételes (ÁFA-kategóriánkénti) bontás lekérése a NAV eNyugtából.
  async enyugtaNyit(n: any) {
    if (!n) return;
    if (!n.id) { this.uzen('Ehhez a tételhez a NAV nem adott azonosítót, így nincs tételes bontás.', true); return; }
    if (n.nyitva) { n.nyitva = false; return; }
    this.enyugtaNapok.forEach((x) => { if (x !== n) x.nyitva = false; });
    n.nyitva = true;
    if (n.detail || n.detailFut) return;
    n.detailFut = true; n.detailHiba = '';
    try {
      n.detail = await this.naplo.get(`/enyugta-detail/${this.ugyfelId}?id=${encodeURIComponent(n.id)}`);
    } catch (e: any) { n.detailHiba = e?.error?.error || 'A tételes lekérés nem sikerült.'; }
    finally { n.detailFut = false; }
  }

  constructor(private naplo: NaploService, private data: DataService, private toast: ToastController, private alert: AlertController) {}

  async ngOnInit() {
    this.alapIdoszak();
    await this.cacheBetolt();
  }
  ngOnDestroy() { this.el = false; }

  //  --- Nyugta-összesítők (Számla és Kassza napi nyugta-adatszolgáltatása) ---
  async nezetNyugta() { this.nezet = 'nyugta'; if (!this.nyugtak.length) await this.nyugtaBetolt(); }
  async nyugtaBetolt() {
    if (!this.ugyfelId) return;
    this.nyugtaBetoltes = true;
    try { this.nyugtak = (await this.naplo.get(`/nyugta/${this.ugyfelId}?ev=${this.ev}`)) || []; }
    catch { this.nyugtak = []; }
    finally { this.nyugtaBetoltes = false; }
  }
  async nyugtakKonyvel(ids?: string[]) {
    const lista = ids && ids.length ? ids : this.nyugtak.filter((n) => !n.konyvelt).map((n) => n.id);
    if (!lista.length) { this.uzen('Minden nyugta-összesítő könyvelve van.'); return; }
    const a = await this.alert.create({
      header: 'Felvezetés a könyvelésbe',
      message: `${lista.length} napi nyugta-összesítő könyvelése bevételként (az ügyfél könyvelési módja szerint, ÁFA-kulcsonként)?`,
      buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Könyvelés', role: 'ok' }],
    });
    await a.present(); const { role } = await a.onDidDismiss(); if (role !== 'ok') return;
    this.nyugtaFut = true;
    try {
      const r: any = await this.naplo.post(`/nyugta/konyvel/${this.ugyfelId}`, { ids: lista });
      await this.nyugtaBetolt();
      if (r.hibak?.length) this.uzen(r.hibak[0], true);
      else this.uzen(`${r.konyvelve || 0} nyugta-összesítő könyvelve${r.cel ? ' (' + r.cel + ')' : ''}${r.kihagyva?.length ? ', ' + r.kihagyva.length + ' kihagyva' : ''}.`);
    } catch (e: any) { this.uzen(e?.error?.error || 'A nyugta könyvelése nem sikerült.', true); }
    finally { this.nyugtaFut = false; }
  }

  private alapIdoszak() {
    const ma = new Date().toISOString().slice(0, 10);
    const vege = `${this.ev}-12-31`;
    this.idoszak = { tol: `${this.ev}-01-01`, ig: vege < ma ? vege : ma };
  }

  /** A táblázat dátumszűrője (a számla kelte szerint, YYYY-MM-DD) – független a NAV-lekérés időszakától. */
  szuroTol = '';
  szuroIg = '';
  get lathato(): any[] {
    const k = this.keresoSzoveg.trim().toLowerCase();
    const tol = this.szuroTol || '', ig = this.szuroIg || '';
    return this.szamlak.filter((s) => (!this.csakKonyveletlen || this.elonezet || (!this.konyveltE(s) && !this.elozoEvi(s) && !s.nem_konyvelheto))
      && (!k || `${s.szamlaszam} ${s.partner_nev || ''} ${s.partner_adoszam || ''}`.toLowerCase().includes(k))
      && (!tol || String(s.kelt || '').slice(0, 10) >= tol) && (!ig || String(s.kelt || '').slice(0, 10) <= ig));
  }
  konyveltE(s: any): boolean { return this.konyveltSzamlak ? this.konyveltSzamlak.has(s.szamlaszam) : !!s.konyvelt; }
  csatoltE(s: any): boolean { return this.csatoltSzamlak ? this.csatoltSzamlak.has(s.szamlaszam) : !!s.csatolt; }
  /*  MÓDOSÍTÓ SZÁMLA 0 Ft-tal: nincs mit könyvelni, de az eredetivel együtt érvényes – csatolni kell. */
  nullasModosito(s: any): boolean {
    return String(s?.muvelet || '').toUpperCase() === 'MODIFY' && !!s?.eredeti_szamlaszam && Math.abs(Number(s.brutto) || 0) < 0.005;
  }
  /*  ELŐZŐ ÉVI SZÁMLA (a kelte a könyvelési év előtti): ebben az évben nem könyvelhető – ha nyitva
   *  maradt, a nyitó egyenlegben (az átemelt nyitott tételek között) már benne van; ha nem, az előző
   *  év könyveléséhez tartozik. A lista ezért csak jelöli, a „Könyvelés" gomb nem jelenik meg. */
  elozoEvi(s: any): boolean { const e = Number(String(s?.kelt || '').slice(0, 4)); return !!e && e < Number(this.ev); }
  get konyveletlenDb(): number { return this.szamlak.filter((s) => !this.konyveltE(s) && !this.elozoEvi(s) && !s.nem_konyvelheto).length; }
  get nemKonyvelhetoDb(): number { return this.szamlak.filter((s) => s.nem_konyvelheto).length; }

  /*  „NEM KÖNYVELHETŐ” JELÖLÉS: a számla kikerül a könyvelendők közül (pl. tévesen kiállított vagy más
   *  céghez tartozó számla). Az ok elhagyható; a jelölés visszavonható. */
  async nemKonyvelheto(s: any, ertek: boolean) {
    let ok = '';
    if (ertek) {
      const a = await this.alert.create({
        header: 'Nem könyvelhető számla',
        message: `${s.szamlaszam} – ${s.partner_nev || ''}: kikerül a könyvelendők közül (visszaállítható).`,
        inputs: [{ name: 'ok', type: 'text', placeholder: 'Ok (nem kötelező), pl. tévesen kiállított' }],
        buttons: [{ text: 'Mégse', role: 'cancel' }, { text: 'Jelölés', role: 'ok' }],
      });
      await a.present();
      const r = await a.onDidDismiss();
      if (r.role !== 'ok') return;
      ok = String(r.data?.values?.ok || '').trim();
    }
    try {
      await this.naplo.post(`/osa/nem-konyvelheto/${this.ugyfelId}`, { szamlaszam: s.szamlaszam, irany: s.irany || this.irany, ertek, ok });
      s.nem_konyvelheto = ertek; s.nem_konyvelheto_ok = ertek ? ok || null : null;
      this.kijelolt.delete(s.szamlaszam);
    } catch (e: any) {
      (await this.toast.create({ message: e?.error?.error || 'Nem sikerült menteni.', duration: 2600, color: 'danger' })).present();
    }
  }

  async iranyValt(i: 'inbound' | 'outbound') {
    if (i === this.irany) return;
    this.irany = i; this.kijelolt.clear(); this.elonezet = false;
    await this.cacheBetolt();
  }

  async cacheBetolt() {
    if (!this.ugyfelId) return;
    this.betoltes = true;
    try {
      const r: any = await this.naplo.get(`/osa/cache/${this.ugyfelId}?direction=${this.irany}`);
      this.szamlak = r?.szamlak || [];
    } catch { this.szamlak = []; }
    finally { this.betoltes = false; }
    this.vevokKepbol();
  }

  /*  MAGÁNSZEMÉLY VEVŐK: a NAV nem adja át a nevüket – a számlaképből olvassuk ki (háttérfeladat,
   *  szkennelt képnél OCR). Kimenő listánál magától indul, ha van név nélküli, képpel bíró számla;
   *  ügyfelenként munkamenetenként egyszer (utána a NAV-lekérés végén úgyis lefut). */
  private static vevoKesz = new Set<string>();
  vevoFeladat: string | null = null;
  private async vevokKepbol() {
    if (this.irany !== 'outbound' || !this.ugyfelId || this.vevoFeladat || OsaValasztoComponent.vevoKesz.has(this.ugyfelId)) return;
    const van = this.szamlak.some((s) => !s.partner_nev && (s.nav_pdf_id || s.dok_pdf_id));
    if (!van) return;
    OsaValasztoComponent.vevoKesz.add(this.ugyfelId);
    try {
      const id = await this.naplo.feladatIndit(`/osa/partner-kepbol-inditas/${this.ugyfelId}`, {});
      if (!id) return;
      this.vevoFeladat = id;
      const f = await this.naplo.feladatVar(id, () => {});
      this.vevoFeladat = null;
      const e = f?.eredmeny || {};
      if (!this.el) return;
      if (e.potolt) { this.uzen(`${e.potolt} magánszemély vevő kiolvasva a számlaképből.`); if (this.irany === 'outbound') await this.cacheBetolt(); }
    } catch { this.vevoFeladat = null; }
  }

  /** Lekérés a NAV-tól háttérfeladatként. Előnézetben az eredmény nem kerül a tárolt listába. */
  async navLekeres(elonezet = this.elonezet, automatikus = false) {
    if (!this.ugyfelId || this.feladatId) return;
    const irany = this.irany;
    try {
      const id = await this.naplo.feladatIndit(`/osa/digest-inditas/${this.ugyfelId}`, {
        direction: irany, dateFrom: this.idoszak.tol, dateTo: this.idoszak.ig, reszletes: true, elonezet,
      });
      if (!id) { if (!automatikus) this.uzen('Nem sikerült elindítani a NAV-lekérést.', true); return; }
      this.feladatId = id;
      this.navUzenet = automatikus ? 'Friss számlák lekérése a NAV-tól…' : 'Lekérés a NAV-tól…';
      const f = await this.naplo.feladatVar(id, (h) => { this.halad = h; });
      this.feladatId = null; this.halad = null;
      if (!this.el || this.irany !== irany) return;
      const e = f?.eredmeny || {};
      if (elonezet && Array.isArray(e.szamlak)) {
        this.szamlak = e.szamlak; this.kijelolt.clear();
        const uj = this.szamlak.filter((s: any) => !s.mar_beemelve).length;
        this.navUzenet = `${this.szamlak.length} számla az időszakban, ${uj} még nincs beemelve – jelöld ki, melyiket kéred.`;
      } else {
        const elotte = this.szamlak.length;
        await this.cacheBetolt();
        const uj = Math.max(0, this.szamlak.length - elotte);
        this.navUzenet = `NAV-lekérés kész (${this.idoszak.tol} – ${this.idoszak.ig})` + (uj ? `: ${uj} új számla.` : ', nincs új számla.');
      }
      if (e.hibas?.length) this.uzen(`${e.hibas.length} számla részletes adata nem jött le (a lista attól még teljes).`, true);
      /*  Számlaképek: ha az ügyfélnél be van kapcsolva az automatikus letöltés, a lekérés után a
       *  NAV Számlázóból is lehoztuk a hiányzó képeket – az eredményt itt mondjuk el. */
      if (e.vevok?.potolt) this.uzen(`${e.vevok.potolt} magánszemély vevő kiolvasva a számlaképből.`);
      const k = e.kepek;
      if (k) {
        if (k.hiba) this.uzen(`Számlaképek: ${k.hiba}`, true);
        else if (k.letoltve) this.uzen(`${k.letoltve} számlakép letöltve a NAV Számlázóból.`);
        else if (k.kert) this.uzen(`Számlakép nem jött le (${k.kert} kértből) – csak a NAV Számlázóban kiállított számlához van kép.`, true);
      }
    } catch (e: any) {
      this.feladatId = null; this.halad = null;
      this.navUzenet = '';
      // Automatikus lekérésnél (pl. nincs még NAV-kulcs) csendben a tárolt lista marad.
      if (!automatikus) this.uzen(e?.error?.error || e?.message || 'A NAV-lekérés nem sikerült.', true);
      else this.navUzenet = 'A NAV-lekérés most nem sikerült – a tárolt számlák látszanak. (NAV technikai felhasználó beállítva?)';
    }
  }

  kijelolValt(s: any) {
    if (this.kijelolt.has(s.szamlaszam)) this.kijelolt.delete(s.szamlaszam); else this.kijelolt.add(s.szamlaszam);
  }
  kijelolMind() {
    const l = this.lathato.filter((s) => this.elonezet || (!this.konyveltE(s) && !s.nem_konyvelheto));
    if (l.length && l.every((s) => this.kijelolt.has(s.szamlaszam))) this.kijelolt.clear();
    else l.forEach((s) => this.kijelolt.add(s.szamlaszam));
  }

  async beemel() {
    const szamlaszamok = Array.from(this.kijelolt);
    if (!szamlaszamok.length) { await this.uzen('Jelöld ki, melyik számlákat emeljem be.', true); return; }
    this.beemelFut = true;
    try {
      const r: any = await this.naplo.post(`/osa/beemel/${this.ugyfelId}`, { direction: this.irany, szamlaszamok });
      this.uzen(`${r?.beemelve || 0} számla beemelve.` + (r?.hibak?.length ? ` (${r.hibak.length} sikertelen)` : ''));
      this.kijelolt.clear(); this.elonezet = false;
      await this.cacheBetolt();
    } catch (e: any) { this.uzen(e?.error?.error || 'A beemelés nem sikerült.', true); }
    finally { this.beemelFut = false; }
  }

  csoportos() {
    const l = this.szamlak.filter((s) => this.kijelolt.has(s.szamlaszam) && !this.konyveltE(s) && !this.elozoEvi(s));
    if (!l.length) { this.uzen('Jelölj ki legalább egy (még nem könyvelt) számlát.', true); return; }
    this.tomegesKonyvel.emit(l);
    this.kijelolt.clear();
  }

  async partnerekKinyer() {
    const lista = this.szamlak.filter((s) => s.partner_nev).map((s) => ({ nev: s.partner_nev, adoszam: s.partner_adoszam }));
    if (!lista.length) { await this.uzen('Nincs partner a listában.', true); return; }
    try {
      const r: any = await this.naplo.post(`/osa/partnerek-kinyer/${this.ugyfelId}`, { partnerek: lista });
      this.uzen(`Partnerek felvéve a partnertörzsbe: ${r?.letrehozott ?? 0} új.`);
    } catch (e: any) { this.uzen(e?.error?.error || 'Nem sikerült felvenni a partnereket.', true); }
  }

  navPdf(s: any) { if (s?.nav_pdf_id) this.data.megnyitFajl('/api/naplo/nav/szamlazo/pdf/' + s.nav_pdf_id, 'NAV számla PDF'); }
  /*  SZÁMLAKÉP LETÖLTÉSE – HÁTTÉRBEN.
   *  A NAV Számlázóból a kép böngészős munkamenettel jön (KAÜ-belépés), ezért – mint az
   *  OSA-lekérés – háttérfeladat: a fejléc állapotjelzőjén követhető, és el lehet navigálni.
   *  Ha megvan, rögtön meg is nyitjuk. */
  kepFeladat: string | null = null;
  async kepLetolt(s: any) {
    const u = (this.naplo?.aktiv?.ugyfelId) || '';
    if (!u || !s?.szamlaszam || this.kepFeladat) return;
    const { szamlazo, osa } = await this.kepLehetosegek(u);
    //  OSA (NAV Számlázó) csak akkor, ha az OSA-számlakép ENGEDÉLYEZETT az ügyfélnél (kep_auto).
    if (osa) {
      try {
        const id = await this.naplo.feladatIndit(`/nav/szamlazo/kepek-inditas/${u}`, { szamlaszamok: [s.szamlaszam] });
        if (!id) { this.uzen('Nem sikerült elindítani a számlakép letöltését.', true); return; }
        this.kepFeladat = id;
        this.uzen(`${s.szamlaszam}: számlakép letöltése a háttérben…`);
        const f = await this.naplo.feladatVar(id, (h) => { this.halad = h; });
        this.kepFeladat = null; this.halad = null;
        const e = f?.eredmeny || {};
        if (e.letoltve) {
          await this.cacheBetolt();
          this.data.megnyitFajl(`/api/naplo/nav/szamlazo/pdf-szamlaszam/${u}?szamlaszam=${encodeURIComponent(s.szamlaszam)}`, 'Számlakép');
        } else {
          this.uzen(e.hiba || `${s.szamlaszam}: nincs kép a NAV Számlázóban (csak az ott kiállított számlához van).`, true);
        }
      } catch (e: any) {
        this.kepFeladat = null; this.halad = null;
        this.uzen(e?.error?.error || 'A számlakép letöltése nem sikerült.', true);
      }
      return;
    }
    //  OSA nincs engedélyezve: ha van bepipált számlázóprogram, onnan hozzuk; különben nem töltünk.
    if (szamlazo) {
      try {
        const r: any = await this.naplo.post(`/szamlazo/${u}/letoltes`, { szamlaszamok: [s.szamlaszam], kenyszer: true });
        const fid = r?.feladat_id || r?.feladatId || null;
        if (fid) { this.kepFeladat = fid; await this.naplo.feladatVar(fid, (h) => { this.halad = h; }); this.kepFeladat = null; this.halad = null; }
        await this.cacheBetolt();
        const uj = this.szamlak.find((x: any) => x.szamlaszam === s.szamlaszam);
        if (uj?.dok_pdf_id) this.dokPdf(uj);
        else this.uzen(`${s.szamlaszam}: számlakép letöltése a számlázóprogramból elindult.`);
      } catch (e: any) {
        this.kepFeladat = null; this.halad = null;
        this.uzen(e?.error?.error || 'A számlakép letöltése nem sikerült.', true);
      }
      return;
    }
    this.uzen('Nincs engedélyezett számlakép-forrás ennél az ügyfélnél (sem bepipált számlázóprogram, sem engedélyezett OSA-számlakép).', true);
  }

  /*  Melyik számlakép-forrás ENGEDÉLYEZETT az ügyfélnél:
   *   - szamlazo: van aktív, mentett kulcsú számlázóprogram-kapcsolat (Billingo / Számlázz.hu);
   *   - osa: a NAV Számlázó (OSA) számlakép-letöltés engedélyezve van (kep_auto).
   *  Ha egyik sem, NEM töltünk – nem próbálunk vakon az OSA-ból (pl. Kovács Patriknál OSA kikapcsolva). */
  private async kepLehetosegek(u: string): Promise<{ szamlazo: boolean; osa: boolean }> {
    let szamlazo = false; let osa = false;
    try {
      const sz: any = await this.naplo.get(`/szamlazo/${u}`);
      szamlazo = (sz?.kapcsolatok || []).some((k: any) => k.aktiv && k.van_kulcs);
    } catch { /* nincs / nem jött le a kapcsolat-lista */ }
    try {
      const t: any = await this.naplo.get(`/nav-tech/${u}`);
      osa = !!t?.kep_auto;
    } catch { /* nincs OSA-beállítás */ }
    return { szamlazo, osa };
  }

  /*  Az egész listához (időszakhoz): a hiányzó számlaképek letöltése háttérben – A BEPIPÁLT FORRÁSBÓL.
   *  Sorrend: (a) ha van aktív bepipált számlázóprogram (Számlázz.hu / Billingo) → onnan;
   *  (b) különben, ha az OSA-számlakép ENGEDÉLYEZETT (kep_auto) → NAV Számlázó;
   *  (c) ha egyik sincs → NEM töltünk (nem próbálunk vakon az OSA-ból, pl. Patriknál). */
  async kepekLetolt() {
    const u = (this.naplo?.aktiv?.ugyfelId) || '';
    if (!u || this.kepFeladat) return;
    const { szamlazo, osa } = await this.kepLehetosegek(u);

    if (szamlazo) {
      //  Számlázóprogram (a bepipált forrás): saját háttérfeladat, a fejléc jelzőjén követhető.
      try {
        //  kenyszer: true – mint az egyenkénti letöltésnél: a korábban „nincs"-választ adott
        //  számlákat is ÚJRA megkérdezi (enélkül a tömeges kihagyta őket, ezért nem töltött).
        const r: any = await this.naplo.post(`/szamlazo/${u}/letoltes`, { kenyszer: true });
        this.uzen(r?.mar_futott
          ? 'A számlaképek letöltése már fut – a fejléc jelzőjén követhető.'
          : 'A számlaképek letöltése elindult a számlázóprogramból – a fejléc jelzőjén követhető.');
        const fid = r?.feladat_id || r?.feladatId || null;
        if (fid) { this.kepFeladat = fid; await this.naplo.feladatVar(fid, (h) => { this.halad = h; }); this.kepFeladat = null; this.halad = null; }
        await this.cacheBetolt();
      } catch (e: any) {
        this.kepFeladat = null; this.halad = null;
        this.uzen(e?.error?.error || 'A számlaképek letöltése nem sikerült.', true);
      }
      return;
    }

    if (!osa) {
      this.uzen('Nincs engedélyezett számlakép-forrás ennél az ügyfélnél (sem bepipált számlázóprogram, sem engedélyezett OSA-számlakép).', true);
      return;
    }

    //  — NAV Számlázó (OSA): az OSA-számlakép engedélyezve van (kep_auto), böngészős lekérés —
    const szamlaszamok = this.szamlak.filter((x: any) => x.irany === 'outbound' || this.irany === 'outbound').map((x: any) => x.szamlaszam).filter(Boolean);
    if (!szamlaszamok.length) { this.uzen('Nincs kimenő számla a listában.', true); return; }
    try {
      const id = await this.naplo.feladatIndit(`/nav/szamlazo/kepek-inditas/${u}`, { szamlaszamok });
      if (!id) { this.uzen('Nem sikerült elindítani a letöltést.', true); return; }
      this.kepFeladat = id;
      this.uzen(`${szamlaszamok.length} számla képének letöltése a háttérben…`);
      const f = await this.naplo.feladatVar(id, (h) => { this.halad = h; });
      this.kepFeladat = null; this.halad = null;
      const e = f?.eredmeny || {};
      await this.cacheBetolt();
      if (e.hiba) this.uzen(`Számlaképek: ${e.hiba}`, true);
      else this.uzen(`${e.letoltve || 0} számlakép letöltve${e.kert ? ` (${e.kert} hiányzóból)` : ''}.`);
    } catch (e: any) {
      this.kepFeladat = null; this.halad = null;
      this.uzen(e?.error?.error || 'A számlaképek letöltése nem sikerült.', true);
    }
  }
  dokPdf(s: any) { if (s?.dok_pdf_id) this.data.megnyitFajl('/api/naplo/dokumentum/pdf/' + s.dok_pdf_id, 'Számlakép'); }
  eredeti(s: any) { if (s?.eredeti_szamlaszam) this.kep.emit({ szamlaszam: s.eredeti_szamlaszam, irany: s.irany }); }

  szam(v: any): string {
    const n = Number(v);
    if (!isFinite(n)) return '';
    return (n < 0 ? '−' : '') + Math.abs(Math.round(n)).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  }

  private async uzen(message: string, hiba = false) {
    (await this.toast.create({ message, duration: hiba ? 3500 : 2500, color: hiba ? 'danger' : 'success', position: 'top' })).present();
  }
}
